import axios from 'axios';
import { handleUnauthorized } from './apiClient';

let installed = false;

function isAuthEndpoint(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.includes('/api/account/signin') || lower.includes('/api/account/signup');
}

// Pulls the raw token out of an "Authorization: Bearer <token>" header value. Every call in
// this app sends Bearer, including a call with a missing/empty credential ("Bearer " with
// nothing after it, e.g. ToolboxLanding.tsx when toolboxToken hasn't been fetched yet) - that
// case deliberately returns null here, same as no header at all.
function extractBearerToken(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^Bearer\s+(\S.*)$/i.exec(value);
  return match ? match[1].trim() : null;
}

function bearerFromHeaders(headers: HeadersInit | undefined): string | null {
  if (!headers) return null;
  return extractBearerToken(new Headers(headers).get('Authorization'));
}

// A relative URL is served by this app's own origin (same-origin locally through the CRA dev
// proxy, or directly in a real deployment). An absolute URL naming a different host is always
// some other service - Toolbox/NowCerts, DataFabric, the AMS proxy, etc. - even though most of
// them also happen to have "/api/" somewhere in their own path.
function isOwnApiUrl(url: string): boolean {
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) return true;
  const ownBases = [process.env.REACT_APP_Auth_Url, process.env.REACT_APP_AccountAMP_Url]
    .filter((base): base is string => !!base);
  const lower = url.toLowerCase();
  return ownBases.some(base => lower.startsWith(base.toLowerCase()));
}

// Only the user's own Momentum AMP session token failing means they're actually logged out
// here. A 401 from any other credential - the Toolbox/NowCerts token, an AMS token, a
// third-party API key - says nothing about that, even when it's sent to our own API: it means
// that OTHER credential is missing, stale, or was never issued, which is for the feature that
// uses it to handle, not a reason to end the whole session. This subsumes stage-azure's
// userapi-specific fix (userapi calls authenticate with toolboxToken/amsToken, never the
// session token, so failingTokenIsSession already returns false for them) without hardcoding
// an endpoint-name allowlist that a differently-hosted "optional" call could fall outside of.
function failingTokenIsSession(tokenValue: string | null): boolean {
  if (!tokenValue) return false;
  const sessionToken = localStorage.getItem('authToken');
  return !!sessionToken && tokenValue === sessionToken;
}

function shouldHandle401(url: string, tokenValue: string | null): boolean {
  if (!url || isAuthEndpoint(url)) return false;
  if (tokenValue !== null) return failingTokenIsSession(tokenValue);

  // No identifiable bearer token on this call - fall back to treating a 401 from our own API
  // as a real session expiry (e.g. cookie/proxy-based auth), but never for a third-party host.
  const isApiCall = url.toLowerCase().includes('/api/');
  return isApiCall && isOwnApiUrl(url) && !!localStorage.getItem('authToken');
}

export function installGlobalAuthInterceptors(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

    const tokenFromInit = bearerFromHeaders(init?.headers);
    const tokenFromRequest = input instanceof Request ? extractBearerToken(input.headers.get('Authorization')) : null;
    const tokenValue = tokenFromInit ?? tokenFromRequest;

    const response = await originalFetch(input, init);
    if (response.status === 401 && shouldHandle401(requestUrl, tokenValue)) {
      handleUnauthorized();
    }
    return response;
  };

  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      if (status === 401) {
        const url: string = String(error?.config?.url ?? '');
        const rawAuth: string | undefined = error?.config?.headers?.Authorization || error?.config?.headers?.authorization;
        const tokenValue = extractBearerToken(rawAuth ?? null);
        if (shouldHandle401(url, tokenValue)) {
          handleUnauthorized();
        }
      }
      return Promise.reject(error);
    }
  );
}
